import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@material-ui/core";

import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";

// B.6: render the dynamic list of "tick to apply" reward bonus configs.
//
// Props:
//   value:    string[]  — codes currently selected (lives in parent values.bonusCodes)
//   onChange: (codes: string[]) => void
//   disabled: boolean   — gray-out the whole group (e.g. when locked='reward')
//
// The component fetches /rewardBonusConfig/active on mount; admin can edit the table
// via /admin_setting → "Cấu hình thưởng".
const formatHint = (cfg) => {
  if (!cfg) return "";
  if (cfg.type === "override_value") {
    return ` (Áp dụng: ${(cfg.value || 0).toLocaleString("vi-VN")} VND/bài)`;
  }
  if (cfg.type === "percent_add") return ` (+${cfg.value}% tiền)`;
  if (cfg.type === "percent_sub") return ` (-${cfg.value}% tiền)`;
  return "";
};

function RewardBonusCheckboxGroup({ value = [], onChange, disabled = false }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiCall({ ...API_URLS.REWARD_BONUS_CONFIG.getActive() })
      .then(({ response, error }) => {
        if (cancelled) return;
        if (!error && response?.status === 200 && response.data?.success) {
          setConfigs(response.data.data || []);
        } else {
          setConfigs([]);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (code) => {
    const next = value.includes(code) ? value.filter((c) => c !== code) : [...value, code];
    onChange(next);
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8}>
        <CircularProgress size={16} />
        <Typography variant="body2">Đang tải cấu hình thưởng…</Typography>
      </Box>
    );
  }
  if (configs.length === 0) {
    return null; // hide the section entirely when no active bonus configs exist
  }

  return (
    <Box>
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
        Áp dụng tiêu chí thưởng (nếu có)
      </Typography>
      {configs.map((cfg) => {
        // Show a small chip when this config belongs to a mutually-exclusive group AND
        // another ticked code shares the same group. Hint: only one of them is counted.
        const groupSiblings = cfg.bonusGroup
          ? configs.filter((c) => c.bonusGroup === cfg.bonusGroup && c.code !== cfg.code)
          : [];
        const otherTicked = groupSiblings.some((c) => value.includes(c.code));
        const thisTicked = value.includes(cfg.code);
        return (
          <FormControlLabel
            key={cfg.code}
            style={{ display: "block" }}
            control={
              <Checkbox
                size="small"
                color="primary"
                disabled={disabled}
                checked={thisTicked}
                onChange={() => toggle(cfg.code)}
              />
            }
            label={
              <span style={{ fontSize: 13 }}>
                {cfg.label}
                {cfg.bonusGroup && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      color: otherTicked && thisTicked ? "#d32f2f" : "#1976d2",
                      fontWeight: 500,
                    }}
                  >
                    [Nhóm {cfg.bonusGroup}
                    {otherTicked && thisTicked && " — chỉ tính 1 lần với các mục cùng nhóm"}]
                  </span>
                )}
              </span>
            }
          />
        );
      })}
    </Box>
  );
}

export default RewardBonusCheckboxGroup;
